// CX Video Hub — MUI theme (modernized)
// Requires MUI v5.14+ (v6/v7 compatible). Uses the CX design tokens directly
// so the theme no longer depends on parsing style.css at runtime.

import { alpha, darken, lighten } from '@mui/material/styles';
// Registers the MuiPickers* keys on `ThemeOptions['components']`. Without it
// the MuiPickersOutlinedInput override below is not a known property.
import type {} from '@mui/x-date-pickers/themeAugmentation';

import SohneKraftig from '@/assets/fonts/Sohne/sohne-kraftig.woff2';
import SohneBuch from '@/assets/fonts/Sohne/sohne-buch.woff2';

// ─── CX Video Hub design tokens ────────────────────────────────
export const cx = {
  bg: {
    base: '#09090C',
    surface: '#1F1F25',
    surface2: '#17171C',
    surface3: '#101013',
    elevated: '#27272E',
    elevated2: '#2F2F36',
    overlay: 'rgba(31,31,37,0.5)',
  },
  border: {
    default: '#4A4A55',
    subtle: '#37373F',
    strong: '#40404A',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#CBCBD2',
    tertiary: '#979AA5',
    muted: '#676774',
    onLight: '#09090C',
  },
  // Light end of the neutral ramp. Deliberately role-neutral: these read as
  // bright foreground on dark surfaces, and as the surface itself when a
  // control inverts (see the puck/ball toggle in AutoISOTeamSelector).
  light: {
    100: '#ECECEE',
    200: '#DBDBE1',
  },
  // The design system's two neutral ramps, carried over under their own names.
  //
  // The indices are contrast-aligned rather than lightness-aligned: in *both*
  // ramps 50 is the maximum-contrast foreground for that mode and 950 is that
  // mode's own background. So washDark[50] is near-white while washLight[50] is
  // near-black. That inversion is the point — a control can ask for index 500
  // and get the right neutral in either mode without branching on the mode.
  //
  // washDark restates values that also appear above under semantic names
  // (bg.surface is washDark[850], border.default is washDark[600], …) and in
  // styles/style.css as --wash-*. Kept whole so the design system's own step
  // names resolve; folding the copies together is a separate change.
  washDark: {
    50: '#F3F2F2',
    100: '#ECECEE',
    200: '#DBDBE1',
    300: '#CBCBD2',
    400: '#9797A5',
    500: '#676774',
    600: '#4A4A55',
    650: '#40404A',
    700: '#37373F',
    750: '#2F2F36',
    800: '#27272E',
    850: '#1F1F25',
    900: '#17171C',
    950: '#101013',
  },
  // Cool/blue-tinted, unlike washDark's warm neutrals — the two are not
  // inversions of each other. Nothing consumes this yet: getTheme() in
  // styles/theme.ts still only flips palette.mode.
  washLight: {
    50: '#030712',
    100: '#111827',
    200: '#1F2937',
    300: '#374151',
    400: '#4B5563',
    500: '#6B7280',
    600: '#9CA3AF',
    650: '#B6BCC5',
    700: '#D1D5DB',
    750: '#DBDEE3',
    800: '#E5E7EB',
    850: '#ECEDF0',
    900: '#F3F4F6',
    950: '#F9FAFB',
  },
  blue: { main: '#1643FF', hover: '#3870FF', dark: '#0932EC' },
  red: { main: '#F04438', deep: '#B52934' },
  gold: '#FDB022',
  green: '#12B76A',
  // For machine-generated identifiers — session handlers, ids, hashes — where
  // fixed-width digits make two values comparable at a glance.
  //
  // A system stack, not a webfont: the design calls for Sohne Mono but only
  // sohne-buch and sohne-kraftig are shipped, so this deliberately falls through
  // to whatever the platform provides rather than pulling in a fourth font file.
  fontMono:
    "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace",
};

// Expose the CX tokens on the theme so call sites can read `theme.palette.cx.*`
// with real types instead of casting through `any`.
//
// This augmentation deliberately lives here rather than in a `styles/theme.d.ts`.
// Because `styles/theme.ts` exists, TypeScript treats a sibling `theme.d.ts` as
// that module's declaration file and drops it from the program entirely — which
// is why the previous flat `wash*` augmentation never actually applied. Keeping
// it in a real `.ts` module that the app imports avoids that trap.
declare module '@mui/material/styles' {
  interface Palette {
    cx: typeof cx;
  }

  interface PaletteOptions {
    cx?: typeof cx;
  }
}

const fontSans =
  "'Sohne', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

// Shared label style — the 12px workhorse from the design system
const label = {
  fontFamily: fontSans,
  fontSize: 12,
  lineHeight: '16px',
  letterSpacing: '0.15px',
};

// Single source of truth for form-control height. Every outlined field —
// TextField, Select, Autocomplete, DatePicker — is pinned to this, so a filter
// row lines up regardless of which control it happens to be built from.
// Derived from the label above: 16px line + 10px padding top/bottom + 2px border.
const FIELD_HEIGHT = 38;
const FIELD_PAD_X = 12;
// Right-hand inset reserved for an absolutely-positioned dropdown arrow /
// clear button, measured from the wrapper's edge.
const ARROW_GUTTER = 32;
// `as const` matters: without it this widens to `string` and no longer
// satisfies MUI's `'small' | 'medium'` union in defaultProps.
const FIELD_SIZE = 'small' as const;

// Minimum height of a data-table body row, shared by every MaterialReactTable in
// the app. Applied to the cells rather than the row — see MuiTableContainer.
const ROW_HEIGHT = 51;

export const themeOptions = {
  palette: {
    mode: 'dark',
    primary: { main: cx.blue.main, dark: cx.blue.dark, light: cx.blue.hover, contrastText: '#fff' },
    secondary: { main: cx.text.secondary, contrastText: cx.text.onLight },
    error: { main: cx.red.main },
    warning: { main: cx.gold, contrastText: cx.text.onLight },
    success: { main: cx.green },
    info: { main: cx.blue.hover },
    background: { default: cx.bg.base, paper: cx.bg.surface },
    divider: cx.border.subtle,
    text: {
      primary: cx.text.primary,
      secondary: cx.text.secondary,
      disabled: cx.text.muted,
    },
    action: {
      active: cx.text.secondary,
      hover: alpha('#FFFFFF', 0.06),
      selected: alpha(cx.blue.main, 0.16),
      disabled: alpha('#FFFFFF', 0.3),
      disabledBackground: alpha('#FFFFFF', 0.08),
      focus: alpha(cx.blue.main, 0.24),
    },
    // Namespaced — avoids polluting the palette root like the old `...colors` spread
    cx,
  },

  shape: { borderRadius: 4 }, // panels/inputs per design system (buttons stay pill)

  typography: {
    fontFamily: fontSans,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    button: { ...label, fontWeight: 400, textTransform: 'none' },
    body1: { fontSize: 14, lineHeight: '20px', letterSpacing: '0.15px' },
    body2: { fontSize: 12, lineHeight: '16px', letterSpacing: '0.15px' },
    caption: { fontSize: 11, lineHeight: '14px', letterSpacing: '0.15px' },
    subtitle1: { fontSize: 16, lineHeight: '22px' },
    subtitle2: { fontSize: 12, lineHeight: '16px', fontWeight: 500, letterSpacing: '0.15px' },
    h4: { fontSize: 36, lineHeight: '40px', fontWeight: 500 },
    h5: { fontSize: 20, lineHeight: '28px', fontWeight: 500 },
    h6: { fontSize: 18, lineHeight: '26px', fontWeight: 400 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Sohne';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
          src: url(${SohneBuch}) format('woff2');
        }
        @font-face {
          font-family: 'Sohne';
          font-style: normal;
          font-display: swap;
          font-weight: 500;
          src: url(${SohneKraftig}) format('woff2');
        }

        /* Scrollbars. The UA default is a light track with a grey thumb, which
           reads as a white bar across the bottom of any table wide enough to
           overflow. Set globally rather than per-container: overflow can appear
           on a table, a dialog, a menu or a detail panel, and chasing each one
           is how the default leaks back in. */
        *::-webkit-scrollbar {
          width: 10px;
          height: 10px;
        }
        *::-webkit-scrollbar-track {
          background: ${cx.bg.surface3};
        }
        *::-webkit-scrollbar-thumb {
          background: ${cx.border.default};
          border-radius: 8px;
          /* Inset via a track-colored border, so the thumb reads as floating
             rather than filling the gutter edge to edge. */
          border: 2px solid ${cx.bg.surface3};
        }
        *::-webkit-scrollbar-thumb:hover {
          background: ${cx.text.tertiary};
        }
        *::-webkit-scrollbar-corner {
          background: ${cx.bg.surface3};
        }

        /* Firefox, which has no ::-webkit-scrollbar equivalent. */
        * {
          scrollbar-width: thin;
          scrollbar-color: ${cx.border.default} ${cx.bg.surface3};
        }
      `,
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: cx.bg.base,
          boxShadow: 'none',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { backgroundColor: cx.bg.base },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          // Kill MUI's dark-mode elevation overlay. It is a white
          // linear-gradient painted over the surface via
          // `backgroundImage: var(--Paper-overlay)`, scaled by elevation — so a
          // Dialog, which ships elevation 24 (~16% white), rendered as a washed
          // grey panel instead of the cx.bg.surface it was given. The
          // `elevation: 0` default above cannot prevent it, because Dialog and
          // Menu pass their own elevation explicitly.
          //
          // Depth in this theme comes from a border and a shadow, not from
          // lightening the fill.
          backgroundImage: 'none',
        },
        outlined: { borderColor: cx.border.subtle },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: cx.bg.surface,
          // A flat surface on a near-black backdrop needs an edge of its own,
          // which is what the overlay was standing in for.
          border: `1px solid ${cx.border.subtle}`,
          boxShadow: '0 16px 48px rgba(0,0,0,0.7)',
          backgroundImage: 'none',
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          ...label,
          fontWeight: 400,
          textTransform: 'none',
          borderRadius: 100,
          padding: '9px 16px',
          gap: 4,
          '&.Mui-focusVisible': {
            outline: `2px solid ${alpha(cx.blue.main, 0.6)}`,
            outlineOffset: 2,
          },
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: 11,
          lineHeight: '14px',
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: 14,
          lineHeight: '18px',
        },
        // `fontSize: 1.1em` sizes a MuiSvgIcon, which is sized by font. It does
        // nothing to a lucide icon: that is a bare <svg> carrying width/height
        // attributes, so it stays at lucide's 24px default and makes the button
        // visibly taller than an otherwise identical one using an MUI icon.
        // Sizing those explicitly, scoped by :not(.MuiSvgIcon-root) so MUI icons
        // keep scaling with the button's font on small/large variants.
        startIcon: {
          marginRight: 4,
          '& > *:nth-of-type(1)': { fontSize: '1.1em' },
          '& > svg:not(.MuiSvgIcon-root)': { width: 14, height: 14 },
        },
        endIcon: {
          marginLeft: 4,
          '& > *:nth-of-type(1)': { fontSize: '1.1em' },
          '& > svg:not(.MuiSvgIcon-root)': { width: 14, height: 14 },
        },

        containedPrimary: {
          '&:hover': { backgroundColor: cx.blue.hover },
          '&:active': { backgroundColor: cx.blue.dark },
        },
        containedSecondary: {
          backgroundColor: cx.bg.elevated,
          color: cx.text.secondary,
          '&:hover': { backgroundColor: lighten(cx.bg.elevated, 0.06), color: cx.text.primary },
        },
        containedError: {
          backgroundColor: cx.red.deep,
          '&:hover': { backgroundColor: darken(cx.red.deep, 0.15) },
        },
        containedWarning: {
          '&:hover': { backgroundColor: darken(cx.gold, 0.12) },
        },
        containedSuccess: {
          '&:hover': { backgroundColor: darken(cx.green, 0.12) },
        },

        outlined: {
          borderColor: cx.border.strong,
          color: cx.text.secondary,
          '&:hover': { backgroundColor: alpha('#FFFFFF', 0.04), borderColor: cx.border.default },
        },
        outlinedPrimary: {
          borderColor: cx.blue.main,
          color: cx.blue.hover,
          '&:hover': { backgroundColor: alpha(cx.blue.main, 0.1), borderColor: cx.blue.hover },
        },
        outlinedError: {
          '&:hover': { backgroundColor: alpha(cx.red.main, 0.08) },
        },
        outlinedWarning: {
          '&:hover': { backgroundColor: alpha(cx.gold, 0.08) },
        },
        outlinedSuccess: {
          '&:hover': { backgroundColor: alpha(cx.green, 0.08) },
        },

        text: {
          color: cx.text.secondary,
          '&:hover': { backgroundColor: alpha('#FFFFFF', 0.04) },
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          ...label,
          fontWeight: 500,
          textTransform: 'none',
          borderRadius: 100,
          color: cx.text.secondary,
          backgroundColor: cx.bg.elevated,
          borderColor: cx.border.subtle,
          transition: 'background-color 0.1s ease, color 0.1s ease',
          '&:hover': { backgroundColor: lighten(cx.bg.elevated, 0.06) },
          '&.Mui-selected': {
            backgroundColor: cx.blue.main,
            color: '#fff',
            '&:hover': { backgroundColor: cx.blue.hover },
          },
          '&.Mui-disabled': {
            borderColor: cx.border.subtle,
            color: cx.text.muted,
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          '&:not(:first-of-type), &:not(:last-of-type)': { borderRadius: 100 },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          ...label,
          textTransform: 'none',
          color: cx.text.tertiary,
          minHeight: 40,
          '&.Mui-selected': { color: cx.text.primary },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40 },
        indicator: { backgroundColor: cx.blue.main, height: 2 },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          ...label,
          borderRadius: 100,
          backgroundColor: cx.bg.elevated,
          color: cx.text.secondary,
        },
        // 22px to match StatusBadge, which is the app's other pill. MUI's small
        // Chip is 24px, so a table mixing chips and status badges showed two
        // pill heights two pixels apart — close enough to read as a mistake
        // rather than a distinction.
        sizeSmall: { height: 22 },
        outlined: { borderColor: cx.border.subtle, backgroundColor: 'transparent' },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: { borderRadius: 6, fontSize: 10, lineHeight: '14px' },
      },
    },

    // ─── Form controls ──────────────────────────────────────────
    // One size for every field. `small` is the app-wide default (and what
    // nearly every call site already asked for); the height rules below are
    // what actually guarantee uniformity, since a stray `size` prop at a call
    // site would otherwise reintroduce a second scale.
    MuiTextField: { defaultProps: { size: FIELD_SIZE } },
    MuiFormControl: { defaultProps: { size: FIELD_SIZE } },

    // Every outlined control is two nested boxes: the `.MuiInputBase-root`
    // wrapper and the `input` (or `.MuiSelect-select` div) inside it. MUI pads
    // BOTH, and pads them differently per control — that stacking is what made
    // Select, TextField and Autocomplete each land on a different height.
    //
    // The rule here: the WRAPPER owns all padding, the INNER box is always
    // zero. Height then comes from `minHeight` alone (the wrapper is
    // `inline-flex; align-items: center; box-sizing: border-box`, so content
    // centres and 38px includes the borders) and is identical for every
    // control, whatever MUI's per-component defaults happen to be.
    MuiOutlinedInput: {
      defaultProps: { size: FIELD_SIZE },
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: cx.bg.surface2,
          minHeight: FIELD_HEIGHT,
          paddingLeft: FIELD_PAD_X,
          paddingRight: FIELD_PAD_X,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: cx.border.strong },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: cx.border.default },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: cx.blue.main,
            borderWidth: 1,
          },
        },
        input: { ...label, padding: 0 },
      },
    },

    MuiSelect: {
      defaultProps: { size: FIELD_SIZE },
      styleOverrides: {
        // The wrapper already supplies FIELD_PAD_X, so this only adds the
        // remainder needed to clear the dropdown arrow.
        select: {
          '&.MuiInputBase-input': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: ARROW_GUTTER - FIELD_PAD_X,
            minHeight: 'unset',
          },
        },
      },
    },

    MuiAutocomplete: {
      defaultProps: { size: FIELD_SIZE },
      styleOverrides: {
        // Autocomplete pads the wrapper itself (from its *root* slot, as
        // `.MuiAutocomplete-root .MuiAutocomplete-inputRoot`), which outranks
        // the MuiOutlinedInput rule above. Doubling `&` gets this to three
        // classes so it lands, and restores the shared padding model.
        inputRoot: {
          '&.MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: FIELD_PAD_X,
            // endAdornment (arrow + clear) is absolutely positioned here
            paddingRight: ARROW_GUTTER,
            '& .MuiAutocomplete-input': {
              padding: 0,
            },
          },
        },
      },
    },

    // The date pickers do NOT render a plain TextField. Since
    // @mui/x-date-pickers v8 the accessible DOM structure is the only option,
    // so a DatePicker renders `PickersOutlinedInput` with its own
    // `MuiPickersOutlinedInput-*` classes — none of the MuiOutlinedInput rules
    // above match it. It repeats the same two-box padding (root `0 14px`, the
    // sections container `8.5px 0` at small, stacking to ~42px), so it needs
    // the same treatment restated against its own class names.
    //
    // `size` is restated too: PickersTextField reads `props.size ?? 'medium'`
    // rather than going through FormControl, so the MuiTextField/MuiFormControl
    // defaults above never reach it. Left at 'medium' the sections container
    // pads `16.5px 0` and the field stands ~15px taller than its neighbours.
    MuiPickersTextField: { defaultProps: { size: FIELD_SIZE } },

    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: cx.bg.surface2,
          minHeight: FIELD_HEIGHT,
          paddingLeft: FIELD_PAD_X,
          paddingRight: FIELD_PAD_X,
          '& .MuiPickersOutlinedInput-notchedOutline': { borderColor: cx.border.strong },
          '&:hover .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: cx.border.default,
          },
          // Doubled `.Mui-focused` to outrank MUI's own focus rule, which
          // otherwise restores a 2px outline and breaks parity with the
          // 1px focus ring used by every other field.
          '&.Mui-focused.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: cx.blue.main,
            borderWidth: 1,
          },
        },
        // The visible date text lives in the `sectionsContainer` slot — NOT
        // `input`, which is the visually-hidden mirror input. Addressed as a
        // real slot rather than a descendant selector so it resolves after the
        // component's own `16.5px 0` / `8.5px 0` size variants. Zeroing it
        // restores the shared model: wrapper owns the padding, inner box is 0,
        // height comes from minHeight alone.
        sectionsContainer: { ...label, padding: 0 },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          ...label,
          color: cx.text.tertiary,
          '&.Mui-focused': { color: cx.text.primary },
        },
        // Centre the resting label in a FIELD_HEIGHT box and sit the shrunk one
        // on the border. Left offset tracks FIELD_PAD_X so label and value align.
        // scale stays at MUI's 0.75 — the notched-outline legend width is
        // computed against that factor, so changing it overflows the notch.
        outlined: {
          transform: `translate(${FIELD_PAD_X}px, 11px) scale(1)`,
          '&.MuiInputLabel-shrink': {
            transform: `translate(${FIELD_PAD_X}px, -6px) scale(0.75)`,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          ...label,
          backgroundColor: cx.bg.elevated,
          border: `1px solid ${cx.border.subtle}`,
        },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: cx.border.subtle } },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: cx.bg.surface,
          border: `1px solid ${cx.border.subtle}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.6)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...label,
          '&.Mui-selected': {
            backgroundColor: alpha(cx.blue.main, 0.16),
            '&:hover': { backgroundColor: alpha(cx.blue.main, 0.24) },
          },
        },
      },
    },

    // MUI's Switch is 58x38, of which 12px per side is invisible ripple
    // padding. This is the compact 28x16 track with a 12px thumb.
    //
    // Deliberately geometry-only: the checked track color is left to MUI's own
    // color variants, because call sites use it semantically — GameToggle
    // passes color="success"/"error" so an availability toggle reads green, not
    // brand blue. Hardcoding the mockup's blue here would erase that.
    MuiSwitch: {
      // Zero padding leaves no room for a ripple to look like anything but a clip.
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          width: 28,
          height: 16,
          padding: 0,
          // The thumb and switchBase geometry is restated HERE, as descendants of
          // the root, rather than only in their own slots below.
          //
          // MUI's `size: 'small'` variant lives on the root slot and reaches
          // inward with `& .MuiSwitch-thumb { width: 16 }` and
          // `& .MuiSwitch-switchBase { padding: 4 }`. Those are two-class
          // selectors, so they outrank a one-class `thumb` / `switchBase`
          // override — and a 16px thumb with 4px padding inside this 16px-tall
          // root overflows, dropping the knob below the track. Matching that
          // shape from the root slot wins on ordering (styleOverrides compose
          // after the component's own variants), so a stray size="small" at a
          // call site can no longer break the geometry.
          '& .MuiSwitch-thumb': { width: 12, height: 12 },
          '& .MuiSwitch-switchBase': {
            padding: 2,
            '&.Mui-checked': { transform: 'translateX(12px)' },
          },
        },
        switchBase: {
          padding: 2,
          color: '#FFFFFF',
          // 28 - 12 thumb - 2 padding on each side = 12px of travel.
          '&.Mui-checked': {
            transform: 'translateX(12px)',
            // Restated because MUI's color variants set the thumb to the same
            // hue as the track when checked, which would hide it entirely.
            color: '#FFFFFF',
            // No backgroundColor here on purpose — see the note above. Only the
            // opacity is forced, since MUI dims the checked track to 0.5.
            '& + .MuiSwitch-track': { opacity: 1 },
          },
          '&:hover, &.Mui-checked:hover': { backgroundColor: 'transparent' },
          '&.Mui-disabled .MuiSwitch-thumb': { color: cx.text.tertiary },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.3 },
        },
        thumb: {
          width: 12,
          height: 12,
          boxShadow: 'none',
        },
        track: {
          borderRadius: 100,
          backgroundColor: cx.bg.elevated2,
          opacity: 1,
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        // Both rules are scoped to labels that actually contain a Switch, so
        // Checkbox and Radio labels — whose controls still carry their own
        // padding — are left exactly as they were.
        root: {
          // MUI offsets the row by -11px to cancel the control's ripple
          // padding. The compact switch has none, so the offset would just pull
          // it out of alignment.
          '&:has(.MuiSwitch-root)': { marginLeft: 0 },
          // Restores the gap the 12px ripple padding used to provide.
          // `:not(:empty)` keeps a label="" switch from gaining dead width.
          '& .MuiSwitch-root + .MuiFormControlLabel-label:not(:empty)': { marginLeft: 8 },
        },
      },
    },

    // ─── Tables ─────────────────────────────────────────────────
    // MaterialReactTable is built on plain MUI table primitives, so these two
    // overrides reach every MRT instance in the app at once instead of being
    // repeated per table. Its toolbars are the exception: MRT_TopToolbar and
    // MRT_BottomToolbar render a bare `Box` with no class and no theme slot, so
    // their only styling hook is the per-table `muiTopToolbarProps` prop.
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontSize: 10,
          fontWeight: 500,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          color: cx.text.muted,
          borderBottom: `1px solid ${cx.border.subtle}`,
        },
        body: {
          ...label,
          color: cx.text.secondary,
          // A near-invisible rule: enough to guide the eye across a wide row
          // without drawing a grid over the data.
          borderBottom: `1px solid ${cx.bg.surface2}`,
        },
      },
    },

    // Scoped through the container rather than set on MuiTableCell/MuiTableRow
    // directly. MRT passes its own `sx` to those components for the sticky
    // header and row hover, and a component `sx` outranks a single-class theme
    // override — going one level up buys the extra class needed to win.
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': { backgroundColor: cx.bg.surface3 },
          '& tbody .MuiTableRow-root:hover': { backgroundColor: cx.bg.surface2 },
          // Row height, in one place for every data table.
          //
          // `height` on a table cell behaves as a MINIMUM (CSS 2.1: a cell's
          // height is the greater of its specified and content heights), so a
          // row with a tall cell — a 30px team logo, a wrapped venue name —
          // still grows. `minHeight` would do nothing here: it does not apply
          // to display: table-cell.
          //
          // The tight vertical padding belongs with it: MRT's density default is
          // 1rem top and bottom, which on its own would push a 30px logo row to
          // 62px and overshoot the floor entirely.
          //
          // Detail-panel cells are exempt. With `renderDetailPanel` set, MRT
          // emits a panel row beneath EVERY row and collapses the unexpanded
          // ones to nothing (py: 0, no border, a zero-height Collapse). A cell
          // height is a minimum, so applying ROW_HEIGHT here would re-inflate
          // each of those to a full empty row between the real ones.
          '& .MuiTableCell-body:not(.Mui-TableBodyCell-DetailPanel)': {
            height: ROW_HEIGHT,
            paddingTop: '4px',
            paddingBottom: '4px',
          },
        },
      },
    },
  },
};

export default themeOptions;
